import {
  TextField,
  TextFieldProps,
  InputAdornment,
  IconButton,
  Grid,
} from "@mui/material";
import {
  Control,
  Controller,
  ControllerProps,
  useFormContext,
} from "react-hook-form";
import React, { useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import CustomTooltip from "./customTooltip";
import {
  AlphaNumeric,
  AlphaText,
  EmailText,
  Numeric,
} from "src/components/helper/inputTextType";

export type CustomTextFieldProps = Omit<TextFieldProps, "name"> & {
  validation?: ControllerProps["rules"];
  name: string;
  toolTipTitle?: string;
  control?: Control<any>;
  readOnly?: boolean;
  autoComplete?: string;
  length?: number;
  regex?: string;
  textTransform?: any;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  label?: string;
};

export default function CustomTextField({
  validation = {},
  type,
  required,
  name,
  toolTipTitle,
  readOnly,
  autoComplete,
  length,
  control,
  regex,
  textTransform,
  onChange,
  label,
  ...rest
}: CustomTextFieldProps): JSX.Element {
  const { register } = useFormContext();
  const [inputValue, setInputValue] = useState<string>("");

  const handleClear = () => {
    setInputValue("");
    onChange &&
      onChange({
        target: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    let isValid = true;

    switch (regex) {
      case Numeric:
        isValid = /^[0-9_]*$/.test(inputValue);
        break;
      case AlphaText:
        isValid = /^[a-zA-Z _]*$/.test(inputValue);
        break;
      case AlphaNumeric:
        isValid = /^[a-zA-Z0-9 _]*$/.test(inputValue);
        break;
      case EmailText:
        isValid = /^[a-zA-Z0-9@.]*$/.test(inputValue);
        break;
      default:
        break;
    }

    if (isValid) {
      setInputValue(inputValue);
      onChange && onChange(event);
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={validation}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <Grid container alignItems="center">
          <Grid container alignItems="center">
            <Grid
              item
              sx={{
                fontSize: "14px",
                fontWeight: "300",
                color:'#797979',
                mb: "4px",
                ml: "4px",
                mr: "6px",
              }}
            >
              {label}
            </Grid>
            {toolTipTitle && <CustomTooltip title={toolTipTitle} />}
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              {...rest}
              value={value || ""}
              required={required}
              type={type}
              {...register(name)}
              inputProps={{
                maxLength: length ? length : undefined,
                readOnly: readOnly ? readOnly : false,
                autoComplete: autoComplete ? autoComplete : undefined,
                style: {
                  textTransform: textTransform ? textTransform : "none",
                  margin: "0px",
                },
              }}
              sx={{
                borderRadius: "100px",
                backgroundColor: readOnly ? "#f5f5f5" : "#ffffff",
                "& .MuiInputLabel-root": {
                  color: "#cccccc", // Label color
                },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "100px",
                  fontSize: "16px",
                      fontWeight: "200",

                  "& fieldset": {                        
                    borderRadius: "100px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#cccccc", // Hovered border color
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#cccccc", // Focused border color
                  },
                  "& input": {
                    color: "#000000", // Text color inside input
                  },
                },
              }}
              error={!!error}
              helperText={error ? error.message : rest?.helperText}
              onChange={(event) => {
              //  handleInputChange(event);
                onChange(event); // Pass the event to the Controller's onChange
              }}
              InputProps={{
                endAdornment:
                  inputValue && !readOnly ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClear}>
                        <ClearIcon
                          sx={{ color: "#212B36", fontSize: "medium" }}
                        />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
              }}
            />
          </Grid>
        </Grid>
      )}
    />
  );
}
